<script lang="ts" setup>
	import useAccountApi from '~/services/apis/account'
	import usePermissionsApi from '~/services/apis/permissions'

	const isUpdating = ref(false)
	const { Roles, Gender } = reactor(useUser())
	const { GetTeamMembers, GetTeamMember } = useAccountApi()
	const { GetAllRoles } = usePermissionsApi()
	const { userId } = useRoute().params

	const teamDetails = ref<TeamMembersResponse | null>(null)

	const formData = ref<Record<string, string | number>>({})

	const isFormSame = computed(() => {
		return JSON.stringify(teamDetails.value) === JSON.stringify(formData.value)
	})
	const roleId = computed(
		() => Roles.value?.find(item => item.label === formData.value.role)?.value || String(formData.value.role)
	)

	const updateFormData = () => {
		formData.value = {
			first_name: teamDetails.value?.first_name || '',
			last_name: teamDetails.value?.last_name || '',
			email: teamDetails.value?.email || '',
			phone: teamDetails.value?.phone || '',
			gender: formData.value?.gender || teamDetails.value?.gender || '',
			role: teamDetails.value?.role || ''
		}
	}
	const submitForm = async () => {
		if (isFormSame.value) return null

		resolve(async () => {
			const { data } = await GetTeamMembers<TeamMembersResponse>(
				'PUT',
				{ ...formData.value, role: roleId.value },
				userId as string
			)
			if (data.value) {
				await navigateTo({ path: '/settings', query: { tab: 'team_management' } }, { replace: true })
				useNotification().alert({
					status: 200,
					message: data.value.responseText
				})
			}
		}, isUpdating)
	}

	onMounted(async () => {
		return resolve(async () => {
			GetAllRoles()
			const { data } = await GetTeamMember<TeamMembersResponse>(userId as string)
			if (data.value) {
				teamDetails.value = {
					first_name: data.value?.data?.first_name || '',
					last_name: data.value?.data?.last_name || '',
					email: data.value?.data?.email || '',
					phone: data.value?.data?.phone || '',
					gender: data.value?.data?.gender || '',
					role: data.value?.data?.role || ''
				}
			}
			updateFormData()
		})
	})
	// Watch For changes and update
	watch(teamDetails, updateFormData, {
		deep: true
	})
</script>

<template>
	<a-form :model="formData" :validate-messages="antValidateMessages" @finish="submitForm">
		<div class="flex items-center justify-center">
			<div
				class="relative mb-5 flex h-[100px] w-[100px] items-center rounded-full bg-purple-50 md:h-[120px] md:w-[120px]"
			>
				<div
					class="mx-auto flex h-[90px] w-[90px] items-center justify-center overflow-hidden text-xl font-bold uppercase md:h-[110px] md:w-[110px]"
				>
					{{ teamDetails?.first_name?.[0] }}
					{{ teamDetails?.last_name?.[0] }}
				</div>
			</div>
		</div>
		<div class="md:space-y-3">
			<a-form-item class="!mb-5" label="First name" required name="first_name">
				<a-input
					v-model:value="formData.first_name"
					class="custom-input !bg-gray-50 !px-5"
					placeholder="John Doe"
				/>
			</a-form-item>

			<a-form-item class="!mb-5" label="Last name" required name="last_name">
				<a-input v-model:value="formData.last_name" class="custom-input !bg-gray-50 !px-5" />
			</a-form-item>

			<a-form-item label="Email Address" required :rules="[{ type: 'email' }]" name="email">
				<a-input v-model:value="formData.email" class="custom-input !bg-gray-50 !px-5" />
			</a-form-item>

			<a-form-item class="!mb-5" label="Phone Number" :rules="[{ min: 11, max: 11 }]" required name="phone">
				<a-input v-model:value="formData.phone" v-number="true" class="custom-input !bg-gray-50 !px-5" />
			</a-form-item>

			<a-form-item class="!mb-5" required name="gender" label="Gender">
				<a-select v-model:value="formData.gender" :options="Gender" />
			</a-form-item>

			<a-form-item class="!mb-5" name="role" required label="Role">
				<a-select v-model:value="formData.role" :options="Roles" />
			</a-form-item>
		</div>

		<div class="mt-10">
			<a-button
				html-type="submit"
				:loading="isUpdating"
				:disabled="isFormSame || isUpdating"
				class="default-btn w-full justify-center bg-primary-2 text-white"
			>
				Update Profile
			</a-button>
		</div>
	</a-form>
</template>
